import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import authService from '../../services/auth.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { clearLinkFromAddressBar, readLinkToken } from '../../lib/linkToken';
import { NAVY, NAVY_LIGHT, GOLD, GOLD_LIGHT } from '../../styles/theme';
import { PasswordField, PolicyChecklist } from './NewPasswordFields';
import { policyPasses } from './passwordPolicy';

export default function ResetPassword(): JSX.Element {
  const navigate = useNavigate();
  // Read once, at mount; the address bar is cleared right after (`lib/linkToken`).
  const [token] = useState(() => readLinkToken());
  useEffect(() => clearLinkFromAddressBar(), []);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const passes = useMemo(() => policyPasses(password), [password]);
  const confirmMatches = confirm.length > 0 && password === confirm;
  const allValid = passes.every(Boolean) && password === confirm;
  const submitDisabled = loading || !allValid || !token;
  const inputsDisabled = loading || !token;
  const submitStyle: React.CSSProperties = submitDisabled
    ? { background: '#E5E7EB', color: '#9CA3AF', cursor: 'not-allowed' }
    : {
        background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_LIGHT} 100%)`,
        color: NAVY,
        cursor: 'pointer',
      };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    if (!token) {
      setError('Reset token is missing.');
      return;
    }
    if (!allValid) {
      setError('Password does not meet the policy or does not match the confirmation.');
      return;
    }
    setLoading(true);
    try {
      await authService.resetPassword(token, password);
      setSuccess(true);
      setTimeout(() => navigate('/login', { replace: true }), 2500);
    } catch (err: unknown) {
      setError(getApiErrorMessage(err, 'Invalid or expired token.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-8"
      style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_LIGHT} 100%)` }}
    >
      <div className="w-full max-w-md">
        <div
          className="rounded-2xl p-9"
          style={{ background: '#FFFFFF', boxShadow: '0 32px 80px rgba(0,0,0,0.45)' }}
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-1">Create new password</h2>
          <p className="text-gray-400 text-sm mb-7">
            Choose a secure password. When you reset it, all your active sessions will be closed.
          </p>

          {!token && (
            <div className="mb-5 p-3 rounded-lg text-sm"
                 style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}>
              The link does not contain a token. Request a new one from &quot;Forgot your password?&quot;.
            </div>
          )}

          {success ? (
            <div className="p-4 rounded-lg text-sm"
                 style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#166534' }}>
              <p className="font-medium mb-1">Password updated</p>
              <p className="text-xs">Redirecting you to sign in...</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 rounded-lg text-sm"
                     style={{ background: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626' }}>
                  {error}
                </div>
              )}

              <div>
                <label htmlFor="reset-new-password" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                  New password
                </label>
                <PasswordField
                  id="reset-new-password"
                  value={password}
                  onChange={setPassword}
                  disabled={inputsDisabled}
                  show={showPassword}
                  onToggleShow={() => setShowPassword((s) => !s)}
                />
              </div>

              <div>
                <label htmlFor="reset-confirm-password" className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                  Confirm password
                </label>
                <input
                  id="reset-confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full px-4 py-2.5 text-sm rounded-xl outline-none transition-all"
                  style={{ background: '#F8F9FC', border: '1.5px solid #E8ECF4', color: '#111827' }}
                  placeholder="Repeat the password"
                  required
                  maxLength={128}
                  disabled={inputsDisabled}
                />
              </div>

              <PolicyChecklist passes={passes} confirmMatches={confirmMatches} />

              <button
                type="submit"
                disabled={submitDisabled}
                className="w-full py-3 rounded-xl font-semibold text-sm transition-all mt-2"
                style={submitStyle}
              >
                {loading ? 'Updating...' : 'Reset password'}
              </button>
            </form>
          )}

          <p className="mt-6 text-center text-xs text-gray-400">
            <Link to="/login" className="font-medium" style={{ color: GOLD }}>
              ← Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
